import { Checkbox } from '@creditsights/styleguide-web';
import React, { ChangeEvent, ReactElement, useState, KeyboardEvent } from 'react';
import classNames from 'classnames/bind';
import useDidUpdate from '../../../hooks/common/useDidUpdate';
import { FilterRange } from '../../../models';
import {
  isEnterKey,
  isTabKey,
  isShiftTabKey,
  isEscapeKey,
  isBackspaceKey,
  isDeleteKey,
  isClipboardActionKey,
  isNonNumericKey,
} from '../../../utils/inputEventUtils';
import { normalizeInputValueOnType, normalizeInputValueOnBlur } from './RangeFilterBox.logic';
import styles from './RangeFilterBox.module.scss';

const cx = classNames.bind(styles);

const validInputKeyVerifiers = [
  isTabKey,
  isShiftTabKey,
  isEscapeKey,
  isBackspaceKey,
  isDeleteKey,
  isClipboardActionKey,
];

interface RangeFilterBoxProps {
  isDisabled?: boolean;
  initialRange?: FilterRange;
  max?: number;
  min?: number;
  onChange: (range: FilterRange) => void;
  title: string;
  asPercent?: boolean;
}

const RangeFilterBox = ({
  isDisabled,
  initialRange = {},
  max = 100,
  min = 0,
  onChange,
  title,
  asPercent,
}: RangeFilterBoxProps): ReactElement => {
  const { from: rawInitialValueFrom, to: rawInitialValueTo, excluded: initialValueExcluded = false } = initialRange;
  const initialValueFrom = asPercent && rawInitialValueFrom ? rawInitialValueFrom * 100 : rawInitialValueFrom;
  const initialValueTo = asPercent && rawInitialValueTo ? rawInitialValueTo * 100 : rawInitialValueTo;

  const [inputValueFrom, setInputValueFrom] = useState<number | undefined>(initialValueFrom);
  const [inputValueTo, setInputValueTo] = useState<number | undefined>(initialValueTo);
  const [isExcluded, setIsExcluded] = useState<boolean | undefined>(initialValueExcluded);
  const placeholder = isDisabled ? '-' : `${min} - ${max}`;

  const buildChangePayload = (payload: FilterRange): FilterRange => {
    const from = payload.from ?? inputValueFrom;
    const to = payload.to ?? inputValueTo;

    return {
      excluded: isExcluded,
      ...payload,
      from: asPercent && from ? from / 100 : from,
      to: asPercent && to ? to / 100 : to,
    };
  };

  const toggleExcluded = () => {
    onChange(buildChangePayload({ excluded: !isExcluded }));
    setIsExcluded((wasExcluded) => !wasExcluded);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (isEnterKey(e)) {
      e.preventDefault();
      handleBlur();
      return;
    }

    if (validInputKeyVerifiers.some((verify) => verify(e))) {
      return;
    }

    if (isNonNumericKey(e)) {
      e.preventDefault();
    }
  };

  const handleInputChangeFrom = (e: ChangeEvent<HTMLInputElement>) => {
    const value = normalizeInputValueOnType(e);

    if (value !== null) {
      setInputValueFrom(value);
    }
  };

  const handleInputChangeTo = (e: ChangeEvent<HTMLInputElement>) => {
    const value = normalizeInputValueOnType(e);

    if (value !== null) {
      setInputValueTo(value);
    }
  };

  const handleBlur = () => {
    const from = normalizeInputValueOnBlur(inputValueFrom, min, max, initialValueFrom);
    const to = normalizeInputValueOnBlur(inputValueTo, min, max, initialValueTo);

    if (from != null && to != null && from > to) {
      setInputValueFrom(initialValueFrom);
      setInputValueTo(initialValueTo);
      return;
    }

    setInputValueFrom(from);
    setInputValueTo(to);

    if (from === initialValueFrom && to === initialValueTo) {
      return;
    }

    onChange(buildChangePayload({ from, to, excluded: false }));
  };

  useDidUpdate(() => {
    if (initialValueFrom !== inputValueFrom) {
      setInputValueFrom(initialValueFrom);
    }
    if (initialValueTo !== inputValueTo) {
      setInputValueTo(initialValueTo);
    }
    if (initialValueExcluded !== isExcluded) {
      setIsExcluded(initialValueExcluded);
    }
  }, [initialValueFrom, initialValueTo, initialValueExcluded]);

  return (
    <div className={cx('body')} data-testid="filter-box">
      <div className={cx('header')}>
        <span className={cx('title', isDisabled && 'disabled')}>{title}</span>
        <Checkbox
          aria-label="Exclude"
          checked={isExcluded}
          dataTestId="filter-toggle"
          label="Excl."
          onChange={toggleExcluded}
          toggle
          disabled={isDisabled}
        />
      </div>
      <div className={cx('content')}>
        <input
          className={cx('input')}
          disabled={isDisabled || isExcluded}
          max={max}
          min={min}
          onBlur={handleBlur}
          onChange={handleInputChangeFrom}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          step={1}
          type="number"
          value={inputValueFrom ?? ''}
        />
        <input
          className={cx('input')}
          disabled={isDisabled || isExcluded}
          max={max}
          min={min}
          onBlur={handleBlur}
          onChange={handleInputChangeTo}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          step={1}
          type="number"
          value={inputValueTo ?? ''}
        />
      </div>
    </div>
  );
};

export default RangeFilterBox;
